import React from 'react';
import { GenericTable } from 'components/table.component';
import { EventExported } from 'interfaces/exportData.interface';
import { useTranslation } from 'react-i18next';
import { dayjs } from '@umanforge/common';
import { Tooltip } from 'react-tooltip';
import { BookMarkIcon } from '@common_components/icons/bookMark.icon';
import SavedViewsManager from 'components/extraction/savedViewsManager.component';
import { ExtractionSavedView, ExtractionSavedViewColumnInput, ExtractionSavedViewFilterInput } from '../../interfaces/extractionSavedView.interface';
import { EventTableColumnKey, EVENTS_TABLE_COLUMNS_META } from '../../constants/extraction.constants';
import ColumnsSelector from 'components/extraction/columnsSelector.component';

interface EventsTableProps {
  fetchJsonData: ( page: number, step: number, sort: { key: string; order: 'asc' | 'desc' } ) => Promise<{ total: number; datas: EventExported[] }>;
  renderList: number;
  onRowClick: ( item: EventExported ) => void;
  buildCurrentFiltersAndColumns: () => { filters: ExtractionSavedViewFilterInput[]; columns: ExtractionSavedViewColumnInput[] };
  applySavedView: ( view: ExtractionSavedView ) => void;
  selectedSavedViewId: string | null;
  onChangeSelectedViewId: ( id: string | null ) => void;
  onClearSelection: () => void;
  visibleColumns: EventTableColumnKey[];
  onChangeVisibleColumns: ( columns: EventTableColumnKey[] ) => void;
}

const EventsTable: React.FC<EventsTableProps> = ( {
  fetchJsonData,
  renderList,
  onRowClick,
  buildCurrentFiltersAndColumns,
  applySavedView,
  selectedSavedViewId,
  onChangeSelectedViewId,
  onClearSelection,
  visibleColumns,
  onChangeVisibleColumns,
} ) => {
  const { t } = useTranslation();
  return (
    <GenericTable<EventExported>
      title={ t( 'translation:event_summary' ) }
      getData={ fetchJsonData }
      renderList={ renderList }
      onRowClick={ onRowClick }
      headerRight={(
        <div className="flex flex-col md:flex-row items-stretch md:items-center gap-3 w-full md:w-auto relative">
          <ColumnsSelector
            selectedColumns={visibleColumns}
            onChangeSelectedColumns={onChangeVisibleColumns}
          />

          <SavedViewsManager
            orientation="horizontal"
            dataType="columns"
            getCurrentFiltersAndColumns={buildCurrentFiltersAndColumns}
            onApplyView={applySavedView}
            selectedViewId={selectedSavedViewId}
            onChangeSelectedViewId={onChangeSelectedViewId}
            onClearSelection={onClearSelection}
          />
        </div>
      )}
      columns={EVENTS_TABLE_COLUMNS_META.filter( columnMeta => { return visibleColumns.includes( columnMeta.key ); } ).map( columnMeta => {
        if ( columnMeta.key === 'title' ) {
          return {
            key: columnMeta.key,
            label: t( columnMeta.labelKey ),
            type: 'actions' as const,
            className: 'px-4 py-3 text-gray-900 font-semibold whitespace-nowrap truncate max-w-60',
            getValue: ( item: EventExported ) => {
              const divId = ( ( item.participantName ?? '' ) as string ).replaceAll( ' ', '_' ) + '_' + item.id;
              return (
                <div className='flex gap-1.5 w-fit text-left'>
                  { item.title || '-' }
                  {
                    item.highlightedEvent ?
                      <>
                        <div
                          className='flex w-fit h-full -top-2.5 -right-8 -mt-1.5'
                          id={ divId }
                        >
                          <BookMarkIcon className=' w-[25px] h-[25px] p-1 rounded-full bg-orange-base fill-orange-base text-white' />
                        </div>
                        <Tooltip
                          anchorSelect={ `#${ divId }` }
                          className='z-[1000] max-w-[600px] text-wrap'
                          place="right"
                        >
                          { t( 'translation:highlighted' ) }
                        </Tooltip>
                      </>
                      :
                      null
                  }
                </div>
              );
            },
          };
        }

        if ( columnMeta.key === 'creationDate' || columnMeta.key === 'deliveryDate' || columnMeta.key === 'completedDate' || columnMeta.key === 'archivedDate' ) {
          return {
            key: columnMeta.key,
            label: t( columnMeta.labelKey ),
            type: 'string' as const,
            className: 'px-4 py-3 text-gray-700 whitespace-nowrap',
            getValue: ( item: EventExported ) => {
              const raw = item[ columnMeta.key as keyof EventExported ] as string;
              return raw
                ? dayjs( raw ).format( 'LL' )
                : '-';
            },
          };
        }

        if ( columnMeta.key === 'status' ) {
          return {
            key: columnMeta.key,
            label: t( columnMeta.labelKey ),
            type: 'string' as const,
            className: 'px-4 py-3 text-gray-700 whitespace-nowrap',
            getValue: ( item: EventExported ) => {
              const completed = item.completedDate != null;
              const archived = item.archivedDate != null;

              let statusKey: string = '';
              if ( !completed && !archived ) {
                statusKey = 'not_completed';
              } else if ( !completed && archived ) {
                statusKey = 'not_completed_but_archived';
              } else if ( completed && !archived ) {
                statusKey = 'completed_but_not_archived';
              } else {
                statusKey = 'completed_and_archived';
              }

              return t( `translation:${statusKey}` );
            },
          };
        }

        return {
          key: columnMeta.key,
          label: t( columnMeta.labelKey ),
          type: 'string' as const,
          className: 'px-4 py-3 text-gray-700 whitespace-nowrap',
          getValue: ( item: EventExported ) => {
            const value = item[ columnMeta.key as keyof EventExported ];
            return ( value as string ) || '-';
          },
        };
      } )}
    />
  );
};

export default EventsTable;
